<template>
    <div class="client-dashboard flex flex-col gap-4 min-h-full pb-4">

        <!-- Greeting -->
        <div class="flex items-center justify-between">
            <div>
                <h2 class="text-lg font-bold" style="color: var(--titulo)">Bem-vindo, {{ userName }}</h2>
                <p class="text-xs" style="color: var(--label); opacity: 0.7">{{ today }}</p>
            </div>
            <button class="btn" @click="loadAll" :disabled="loadingAny">
                <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loadingAny }"></i>
                <span style="white-space:nowrap">Atualizar</span>
            </button>
        </div>

        <!-- Stat Cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="stat-card" v-for="card in statCards" :key="card.label">
                <div class="stat-icon">
                    <i :class="card.icon" :style="{ color: card.color }"></i>
                </div>
                <div class="stat-info">
                    <span class="stat-value" :style="{ color: card.color }">
                        <span v-if="card.loading">—</span>
                        <span v-else>{{ card.value }}</span>
                    </span>
                    <span class="stat-label">{{ card.label }}</span>
                </div>
            </div>
        </div>

        <!-- Tables Row -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">

            <!-- Próximos Agendamentos -->
            <div class="dash-panel">
                <div class="dash-panel-header">
                    <i class="fa-regular fa-calendar-check" style="color: var(--amarelo-2)"></i>
                    <span>Próximos Agendamentos</span>
                    <span v-if="loadingAgendamentos" class="loading-dot"></span>
                </div>
                <div class="dash-panel-body">
                    <div v-if="agendamentos.length === 0 && !loadingAgendamentos" class="empty-state">
                        Sem agendamentos próximos
                    </div>
                    <table v-else class="dash-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Técnico</th>
                                <th>Local</th>
                                <th>Família</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in agendamentos" :key="item.id">
                                <td style="white-space:nowrap">{{ formatDate(item.data || item.inicio) }}</td>
                                <td>{{ item.tecnico || '—' }}</td>
                                <td>{{ item.local || item.subcliente || '—' }}</td>
                                <td>{{ item.familia || '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Últimos Relatórios -->
            <div class="dash-panel">
                <div class="dash-panel-header">
                    <i class="fa-solid fa-file-lines" style="color: var(--amarelo-2)"></i>
                    <span>Últimos Relatórios</span>
                    <span v-if="loadingRelatorios" class="loading-dot"></span>
                </div>
                <div class="dash-panel-body">
                    <div v-if="relatorios.length === 0 && !loadingRelatorios" class="empty-state">
                        Sem relatórios recentes
                    </div>
                    <table v-else class="dash-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Família</th>
                                <th>Local</th>
                                <th>PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in relatorios" :key="item.id">
                                <td style="white-space:nowrap">{{ formatDate(item.data_inicio || item.data) }}</td>
                                <td>{{ item.familia || '—' }}</td>
                                <td>{{ item.local || item.subcliente || '—' }}</td>
                                <td>
                                    <button
                                        class="btn"
                                        style="display:inline-flex;gap:5px;padding:2px 8px;"
                                        @click="openRelatorioPdf(item)"
                                    >
                                        <i class="fa-solid fa-file-pdf"></i>
                                        <span style="white-space:nowrap">Ver PDF</span>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Calendário de Ordens de Serviço -->
        <div class="dash-panel">
            <div class="dash-panel-header">
                <i class="fa-regular fa-calendar-days" style="color: var(--amarelo-2)"></i>
                <span>Ordens de Serviço</span>
                <span v-if="loadingCalendario" class="loading-dot"></span>
            </div>
            <div class="dash-panel-body">
                <div class="os-calendar">
                    <FullCalendar ref="calendar" :options="calendarOptions" />
                </div>
                <div class="cal-legend">
                    <span v-for="item in legendaStatus" :key="item.status" class="cal-legend-item">
                        <span class="cal-legend-dot" :style="{ background: item.color }"></span>{{ item.label }}
                    </span>
                </div>
            </div>
        </div>

    </div>

    <PdfPreviewDialog v-if="show_pdf_preview" :pdfUrl="pdfUrl" @close="show_pdf_preview = false" />

    <!-- Detalhe da Ordem de Serviço -->
    <div v-if="osDetalhe" class="os-modal-overlay" @click.self="osDetalhe = null">
        <div class="os-modal-card">
            <div class="os-modal-header">
                <span>
                    <i class="fa-solid fa-screwdriver-wrench" style="color: var(--amarelo-2)"></i>
                    Serviço {{ osDetalhe.negocio || '—' }}
                </span>
                <button type="button" @click="osDetalhe = null" title="Fechar">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>
            <div class="os-modal-body">
                <div class="os-detail-row">
                    <span class="os-detail-label">Estado</span>
                    <span class="status-badge" :class="osDetalhe.status">{{ statusLabel(osDetalhe.status) || '—' }}</span>
                </div>
                <div class="os-detail-row">
                    <span class="os-detail-label">Data</span>
                    <span>{{ formatDateTimeRange(osDetalhe) }}</span>
                </div>
                <div class="os-detail-row">
                    <span class="os-detail-label">Família</span>
                    <span>{{ osDetalhe.familia || '—' }}</span>
                </div>
                <div class="os-detail-row">
                    <span class="os-detail-label">Local</span>
                    <span>{{ osDetalhe.local || '—' }}</span>
                </div>
                <div class="os-detail-row">
                    <span class="os-detail-label">Sub-cliente</span>
                    <span>{{ osDetalhe.subcliente || '—' }}</span>
                </div>
                <div class="os-detail-row">
                    <span class="os-detail-label">Técnico</span>
                    <span>{{ osDetalhe.tecnico || '—' }}</span>
                </div>
                <!-- O PDF só fica disponível em serviços fechados com relatório definitivo -->
                <div class="os-detail-row" v-if="osDetalhe.status === 'fechado'">
                    <span class="os-detail-label">Relatório</span>
                    <span v-if="loadingRelatorioOs" style="opacity: 0.6">A procurar...</span>
                    <button
                        v-else-if="relatorioDefinitivo"
                        class="btn"
                        style="display:inline-flex;gap:5px;padding:2px 8px;justify-self:start;"
                        @click="openRelatorioOsPdf"
                    >
                        <i class="fa-solid fa-file-pdf"></i>
                        <span style="white-space:nowrap">Ver PDF</span>
                    </button>
                    <span v-else style="opacity: 0.6">Sem relatório definitivo</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import PdfPreviewDialog from '@/components/dialog/pdfPreviewDialog.vue';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import ptLocale from '@fullcalendar/core/locales/pt';
import { normalizarDataBD, parseDataBD, hojeLocal } from '@/utils/dataBD';

const STATUS_COLORS = {
    ativo:        '#22c55e',
    agendado:     '#3b82f6',
    fechado:      '#6b7280',
    cancelado:    '#ef4444',
    interrompido: '#f97316',
    aprovado:     '#10b981',
    rejeitado:    '#ef4444',
    pendente:     '#f59e0b',
    inativo:      '#9ca3af',
    atribuido:    '#8b5cf6',
    confirmado:   '#06b6d4',
};

// Estados de planeamento mostrados no calendário do cliente
const CALENDAR_STATUS = ['confirmado', 'fechado'];

// Apenas o texto mostrado ao cliente: o estado em si continua a ser 'confirmado'
const STATUS_LABELS = {
    confirmado: 'agendado',
};

export default {
    name: 'ClientDashboardView',
    components: { FullCalendar, PdfPreviewDialog },

    data() {
        const today = new Date();
        return {
            baseURl: process.env.VUE_APP_URL_BASE,
            userId: null,
            userName: '',
            today: today.toLocaleDateString('pt-PT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),

            // Loading flags
            loadingInstalacoes: true,
            loadingRelatorios: true,
            loadingOrcamentos: true,
            loadingAgendamentos: true,
            loadingCalendario: true,

            // Counters
            totalInstalacoes: 0,
            totalRelatorios: 0,
            totalOrcamentos: 0,
            totalAgendamentos: 0,

            // Table data
            agendamentos: [],
            relatorios: [],

            // PDF preview
            pdfUrl: '',
            show_pdf_preview: false,

            // Calendário de Ordens de Serviço
            osEvents: [],
            // Intervalo visível do calendário (definido pelo datesSet)
            calendarRange: null,
            osDetalhe: null,
            // Relatório definitivo da ordem de serviço aberta no popup
            relatorioDefinitivo: null,
            loadingRelatorioOs: false,
            // id do tpRelatorio 'Definitivo' (resolvido uma vez por sessão da página)
            tpDefinitivoId: null,
        };
    },

    computed: {
        loadingAny() {
            return this.loadingInstalacoes || this.loadingRelatorios || this.loadingOrcamentos
                || this.loadingAgendamentos || this.loadingCalendario;
        },

        calendarOptions() {
            return {
                plugins: [dayGridPlugin],
                locale: ptLocale,
                initialView: 'dayGridMonth',
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: '',
                },
                height: 'auto',
                // 'block' pinta o evento todo com a cor do estado (o default só
                // colore o pontinho nos eventos com hora)
                eventDisplay: 'block',
                dayMaxEventRows: 3,
                firstDay: 1,
                weekends: true,
                displayEventTime: true,
                events: this.osEvents,
                datesSet: this.onDatesSet,
                eventClick: this.onEventClick,
            };
        },

        legendaStatus() {
            return CALENDAR_STATUS.map(status => ({
                status,
                label: this.statusLabel(status),
                color: STATUS_COLORS[status] || '#9ca3af',
            }));
        },
        statCards() {
            return [
                {
                    label: 'Instalações',
                    value: this.totalInstalacoes,
                    loading: this.loadingInstalacoes,
                    icon: 'fa-solid fa-location-dot fa-xl',
                    color: '#3b82f6',
                },
                {
                    label: 'Relatórios',
                    value: this.totalRelatorios,
                    loading: this.loadingRelatorios,
                    icon: 'fa-solid fa-file-lines fa-xl',
                    color: '#22c55e',
                },
                {
                    label: 'Orçamentos',
                    value: this.totalOrcamentos,
                    loading: this.loadingOrcamentos,
                    icon: 'fa-solid fa-file-invoice fa-xl',
                    color: '#f59e0b',
                },
                {
                    label: 'Agendamentos',
                    value: this.totalAgendamentos,
                    loading: this.loadingAgendamentos,
                    icon: 'fa-regular fa-calendar-check fa-xl',
                    color: '#8b5cf6',
                },
            ];
        },
    },

    created() {
        const user = JSON.parse(localStorage.getItem('user'));
        this.userId = user?.id || null;
        this.userName = user?.contacto?.nome || user?.user || 'Cliente';
        if (this.userId) this.loadAll();
    },

    methods: {
        loadAll() {
            this.loadInstalacoes();
            this.loadAgendamentos();
            this.loadRelatorios();
            this.loadOrcamentos();
            // O primeiro carregamento do calendário é feito pelo datesSet, quando o
            // FullCalendar já sabe que intervalo está visível.
            if (this.calendarRange) this.loadOrdensServico();
        },

        async loadInstalacoes() {
            this.loadingInstalacoes = true;
            try {
                const res = await axios.get(`${this.baseURl}/api/v1/localinstalacao/utilizador/${this.userId}/pagination/`, {
                    params: { page_size: 1 },
                });
                this.totalInstalacoes = res.data?.count ?? res.data?.total_count ?? 0;
            } catch { this.totalInstalacoes = 0; }
            finally { this.loadingInstalacoes = false; }
        },

        async loadAgendamentos() {
            this.loadingAgendamentos = true;
            try {
                const today = hojeLocal();
                const res = await axios.get(`${this.baseURl}/api/v1/myplaneamento/utilizador/${this.userId}/TableFilter/pagination/`, {
                    params: { page_size: 5, data_inicio: today },
                });
                const results = res.data?.results ?? res.data?.data ?? [];
                this.agendamentos = results;
                this.totalAgendamentos = res.data?.count ?? res.data?.total_count ?? results.length;
            } catch { this.agendamentos = []; this.totalAgendamentos = 0; }
            finally { this.loadingAgendamentos = false; }
        },

        async loadRelatorios() {
            this.loadingRelatorios = true;
            try {
                const res = await axios.get(`${this.baseURl}/api/v1/myrelatorio/utilizador/${this.userId}/TableFilter/pagination/`, {
                    params: { page_size: 5 },
                });
                const results = res.data?.results ?? res.data?.data ?? [];
                this.relatorios = results;
                this.totalRelatorios = res.data?.count ?? res.data?.total_count ?? results.length;
            } catch { this.relatorios = []; this.totalRelatorios = 0; }
            finally { this.loadingRelatorios = false; }
        },

        async loadOrcamentos() {
            // Só interessa o total para o cartão: 'count' já vem filtrado.
            // 'estado' é o tpEstadoOrcamento do orçamento (10 = 'Orçamento - Adjudicado'),
            // não o campo status ('ativo'/'cancelado').
            this.loadingOrcamentos = true;
            try {
                const res = await axios.get(`${this.baseURl}/api/v1/myorcamento/utilizador/${this.userId}/TableFilter/pagination/`, {
                    params: { page_size: 1, estado: 10, status: 'ativo' },
                });
                const results = res.data?.results ?? res.data?.data ?? [];
                this.totalOrcamentos = res.data?.count ?? res.data?.total_count ?? results.length;
            } catch { this.totalOrcamentos = 0; }
            finally { this.loadingOrcamentos = false; }
        },

        // ---- CALENDÁRIO DE ORDENS DE SERVIÇO ----
        onDatesSet(info) {
            // info.end é exclusivo: recua um dia para o filtro data_fim (data__lte no backend)
            const end = new Date(info.end.getTime() - 86400000);
            this.calendarRange = { inicio: this.toISODate(info.start), fim: this.toISODate(end) };
            this.loadOrdensServico();
        },

        async loadOrdensServico() {
            if (!this.calendarRange || !this.userId) {
                this.loadingCalendario = false;
                return;
            }
            this.loadingCalendario = true;
            try {
                // POST porque só assim é possível filtrar por vários estados (statuss)
                // do lado do servidor, em vez de trazer tudo e cortar aqui.
                const res = await axios.post(
                    `${this.baseURl}/api/v1/myplaneamento/utilizador/${this.userId}/TableFilter/pagination/?page_size=500`,
                    {
                        data_inicio: this.calendarRange.inicio,
                        data_fim: this.calendarRange.fim,
                        statuss: CALENDAR_STATUS,
                    },
                );
                const results = res.data?.results ?? res.data?.data ?? [];
                this.osEvents = results
                    .filter(item => this.temTecnico(item))
                    .map(item => this.buildOsEvent(item));
            } catch { this.osEvents = []; }
            finally { this.loadingCalendario = false; }
        },

        // Só muda o nome mostrado — o estado guardado/filtrado mantém-se
        statusLabel(status) {
            return STATUS_LABELS[status] || status;
        },

        // Serviços sem técnico atribuído não entram no calendário
        temTecnico(item) {
            const tecnico = (item.tecnico || '').trim();
            return tecnico !== '' && tecnico !== '—' && tecnico !== 'N/A';
        },

        buildOsEvent(item) {
            const color = STATUS_COLORS[item.status] || '#9ca3af';
            // Sem hora marcada, o serviço fica como evento de dia inteiro na data planeada
            const allDay = !item.inicio;
            const title = [item.familia, item.local || item.subcliente].filter(Boolean).join(' — ') || 'Serviço';
            // Mantém a hora literal da BD (ver utils/dataBD.js)
            return {
                id: String(item.id),
                title,
                start: item.inicio ? normalizarDataBD(item.inicio) : item.data,
                end: item.inicio ? (normalizarDataBD(item.fim) || undefined) : undefined,
                allDay,
                backgroundColor: color,
                borderColor: color,
                textColor: '#ffffff',
                extendedProps: { ...item },
            };
        },

        onEventClick(info) {
            this.osDetalhe = { ...info.event.extendedProps };
            this.relatorioDefinitivo = null;
            if (this.osDetalhe.status === 'fechado') {
                this.loadRelatorioDefinitivo(this.osDetalhe);
            }
        },

        // O campo 'negocio' vem no formato "negocioId.ordemServicoId"
        ordemServicoId(item) {
            const partes = String(item?.negocio ?? '').split('.');
            return partes.length > 1 ? partes[1] : null;
        },

        async getTpDefinitivoId() {
            if (this.tpDefinitivoId) return this.tpDefinitivoId;
            const res = await axios.get(`${this.baseURl}/api/v1/tprelatorio/`);
            const tipos = res.data?.data ?? [];
            this.tpDefinitivoId = tipos.find(t => (t.nome || '').toLowerCase() === 'definitivo')?.id ?? null;
            return this.tpDefinitivoId;
        },

        async loadRelatorioDefinitivo(item) {
            const osId = this.ordemServicoId(item);
            if (!osId) return;
            this.loadingRelatorioOs = true;
            try {
                const definitivoId = await this.getTpDefinitivoId();
                // 404 (sem relatório) cai no catch e mantém relatorioDefinitivo a null
                const res = await axios.get(`${this.baseURl}/api/v1/relatorio/s/ordemServico_id/${osId}/`, {
                    params: { exact: 1 },
                });
                const encontrado = (res.data?.data ?? []).find(r => r.tpRelatorio_id === definitivoId);
                // Ignora respostas de um serviço que já não é o que está aberto
                if (encontrado && this.ordemServicoId(this.osDetalhe) === osId) {
                    this.relatorioDefinitivo = encontrado;
                }
            } catch { this.relatorioDefinitivo = null; }
            finally { this.loadingRelatorioOs = false; }
        },

        openRelatorioOsPdf() {
            if (!this.relatorioDefinitivo) return;
            this.pdfUrl = `${this.baseURl}/api/v1/relatoriocliente/pdf/${this.relatorioDefinitivo.id}/`;
            this.show_pdf_preview = true;
        },

        toISODate(date) {
            const pad = n => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
        },

        formatDateTimeRange(item) {
            if (!item.inicio) return this.formatDate(item.data);
            const opts = { hour: '2-digit', minute: '2-digit' };
            const inicio = parseDataBD(item.inicio);
            if (!inicio || isNaN(inicio)) return this.formatDate(item.data);
            let out = `${inicio.toLocaleDateString('pt-PT')} ${inicio.toLocaleTimeString('pt-PT', opts)}`;
            const fim = item.fim ? parseDataBD(item.fim) : null;
            if (fim && !isNaN(fim)) out += ` - ${fim.toLocaleTimeString('pt-PT', opts)}`;
            return out;
        },

        openRelatorioPdf(item) {
            this.pdfUrl = `${this.baseURl}/api/v1/relatoriocliente/pdf/${item.id}/`;
            this.show_pdf_preview = true;
        },

        formatDate(str) {
            if (!str) return '—';
            const d = new Date(str);
            if (isNaN(d)) return str;
            return d.toLocaleDateString('pt-PT');
        },
    },
};
</script>

<style scoped>
.client-dashboard {
    padding: 0 0.25rem;
    /* Não encolher dentro do flex do dashboard: quem faz scroll é a página */
    flex-shrink: 0;
}

/* Os painéis têm overflow:hidden (cantos arredondados), o que faz o flex
   considerar a altura mínima deles = 0 e espremê-los até cortar o calendário.
   Cada bloco mantém a altura natural e o excesso vai para o scroll da página. */
.client-dashboard > * {
    flex-shrink: 0;
}

/* Stat Cards */
.stat-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-radius: 12px;
    background: var(--fundoComponente, rgba(255,255,255,0.08));
    border: 1px solid rgba(255,255,255,0.08);
}

.stat-icon {
    font-size: 1.5rem;
    width: 2.5rem;
    text-align: center;
    flex-shrink: 0;
}

.stat-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
}

.stat-label {
    font-size: 0.7rem;
    color: rgba(255,255,255,0.55);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Panels */
.dash-panel {
    border-radius: 12px;
    background: var(--fundoComponente, rgba(255,255,255,0.08));
    border: 1px solid rgba(255,255,255,0.08);
    overflow: hidden;
}

.dash-panel-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--label, rgba(255,255,255,0.9));
    border-bottom: 1px solid rgba(255,255,255,0.06);
}

.dash-panel-body {
    padding: 0.5rem;
    overflow-x: auto;
}

/* Table */
.dash-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.72rem;
    color: var(--text, rgba(255,255,255,0.85));
}

.dash-table th {
    padding: 0.35rem 0.5rem;
    text-align: left;
    color: rgba(255,255,255,0.45);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
}

.dash-table td {
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
}

.dash-table tbody tr:last-child td {
    border-bottom: none;
}

.dash-table tbody tr:hover {
    background: rgba(255,255,255,0.04);
}

/* Status Badge */
.status-badge {
    display: inline-block;
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: capitalize;
    background: rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.7);
}
.status-badge.agendado   { background: rgba(59,130,246,0.2);  color: #93c5fd; }
.status-badge.ativo      { background: rgba(34,197,94,0.2);   color: #86efac; }
.status-badge.aprovado   { background: rgba(16,185,129,0.2);  color: #6ee7b7; }
.status-badge.fechado    { background: rgba(107,114,128,0.2); color: #d1d5db; }
.status-badge.cancelado  { background: rgba(239,68,68,0.2);   color: #fca5a5; }
.status-badge.interrompido { background: rgba(249,115,22,0.2); color: #fdba74; }
.status-badge.pendente   { background: rgba(245,158,11,0.2);  color: #fcd34d; }
.status-badge.atribuido  { background: rgba(139,92,246,0.2);  color: #c4b5fd; }
.status-badge.confirmado { background: rgba(6,182,212,0.2);   color: #67e8f9; }

/* Empty state */
.empty-state {
    padding: 1rem;
    text-align: center;
    font-size: 0.72rem;
    color: rgba(255,255,255,0.3);
}

/* Calendário de Ordens de Serviço */
.os-calendar {
    padding: 0.25rem;
    font-size: 0.75rem;
}

.os-calendar :deep(.fc) {
    --fc-border-color: rgba(255,255,255,0.08);
    --fc-page-bg-color: transparent;
    --fc-neutral-bg-color: rgba(255,255,255,0.04);
    --fc-today-bg-color: rgba(255,155,5,0.12);
    --fc-button-bg-color: rgba(255,255,255,0.08);
    --fc-button-border-color: rgba(255,255,255,0.12);
    --fc-button-hover-bg-color: rgba(255,255,255,0.16);
    --fc-button-hover-border-color: rgba(255,255,255,0.2);
    --fc-button-active-bg-color: var(--amarelo-2, #ff9b05);
    --fc-button-active-border-color: var(--amarelo-2, #ff9b05);
    color: var(--text, rgba(255,255,255,0.85));
}

.os-calendar :deep(.fc .fc-toolbar) {
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-bottom: 0.6rem;
}

.os-calendar :deep(.fc .fc-toolbar-title) {
    font-size: 0.95rem;
    font-weight: 600;
    text-transform: capitalize;
}

.os-calendar :deep(.fc .fc-button) {
    font-size: 0.7rem;
    padding: 0.25rem 0.55rem;
    text-transform: capitalize;
    box-shadow: none;
}

.os-calendar :deep(.fc .fc-col-header-cell-cushion),
.os-calendar :deep(.fc .fc-daygrid-day-number) {
    color: rgba(255,255,255,0.6);
    font-size: 0.7rem;
    text-decoration: none;
}

.os-calendar :deep(.fc .fc-event) {
    cursor: pointer;
    font-size: 0.65rem;
    border-radius: 4px;
    padding: 0 2px;
}

.os-calendar :deep(.fc .fc-daygrid-more-link) {
    font-size: 0.65rem;
    color: var(--amarelo-2, #ff9b05);
}

.os-calendar :deep(.fc .fc-popover) {
    background: #1f2937;
    border: 1px solid rgba(255,255,255,0.12);
    z-index: 60;
}

.cal-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 0.9rem;
    padding: 0.6rem 0.5rem 0.2rem;
    font-size: 0.65rem;
    color: rgba(255,255,255,0.55);
    text-transform: capitalize;
}

.cal-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.cal-legend-dot {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    display: inline-block;
}

/* Detalhe da Ordem de Serviço */
.os-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.os-modal-card {
    width: 100%;
    max-width: 420px;
    border-radius: 12px;
    /* Fundo opaco: o calendário não pode ver-se por trás do popup */
    background: #101526;
    border: 1px solid rgba(255,255,255,0.12);
    box-shadow: 0 12px 32px rgba(0,0,0,0.5);
    overflow: hidden;
}

.os-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--label, rgba(255,255,255,0.9));
    border-bottom: 1px solid rgba(255,255,255,0.08);
}

.os-modal-header button {
    color: rgba(255,255,255,0.5);
}

.os-modal-header button:hover {
    color: rgba(255,255,255,0.9);
}

.os-modal-body {
    padding: 0.75rem 1rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    font-size: 0.72rem;
    color: var(--text, rgba(255,255,255,0.85));
}

.os-detail-row {
    display: grid;
    grid-template-columns: 90px 1fr;
    gap: 0.5rem;
    align-items: start;
}

.os-detail-row .status-badge {
    justify-self: start;
}

.os-detail-label {
    color: rgba(255,255,255,0.45);
    text-transform: uppercase;
    font-size: 0.62rem;
    letter-spacing: 0.4px;
    padding-top: 2px;
}

/* Loading dot */
.loading-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--amarelo-2, #ff9b05);
    animation: pulse-dot 1s ease-in-out infinite;
    margin-left: auto;
}

@keyframes pulse-dot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.2; }
}
</style>
